import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MenuComponent } from '../../shared/components/menu/menu.component';
import { FooterComponent } from '../../shared/components/footer/footer.component';
import { UserRegisterComponent } from '../../shared/components/user-register/user-register.component';
import { AuthService } from '../../shared/services/apis/platform/auth.service';
import { UserService } from '../../shared/services/apis/platform/user.service';
import { PublicSimulatorService } from '../../shared/services/apis/platform/public-simulator.service';
import { BookPrintStruct } from '../../shared/services/responses/platform/book-print.response';
import { BookCostCalc, computeBreakdown, getMinPrice, getVariableRate, getProductionCost, toMoney } from '../../my-books/book-publish-edit.component/book-cost.calculator';
import { Subject } from 'rxjs';
import { debounceTime, take } from 'rxjs/operators';
export interface PrintGroup {
  size: string;
  variations: BookPrintStruct[];
}

@Component({
  selector: 'app-book-simulator',
  standalone: true,
  templateUrl: './book-simulator.component.html',
  styleUrls: ['./book-simulator.component.css'],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MenuComponent,
    FooterComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BookSimulatorComponent implements OnInit {
  isLoadingConfig = true;
  isLoadingPrints = false;

  publicConfig: { platformProfitPercent: number; baseTaxPercent: number; productionCostProfitMarginPercent: number } | null = null;
  listBookPrint: BookPrintStruct[] = [];
  groupedPrints: PrintGroup[] = [];
  selectedSizeGroup: PrintGroup | null = null;
  selectedPrint: BookPrintStruct | null = null;

  model: FormGroup;
  userChangedPrice = false;
  calcMode: 'profit' | 'price' = 'profit';
  calculatedSellPrice: number = 0;

  private pagesSubject = new Subject<number>();

  // Resultados da simulação
  minPrice: number = 0;
  estimatedProfit: number = 0;
  estimatedMargin: number = 0;
  productionCost: number = 0;
  platformCost: number = 0;
  taxCost: number = 0;

  constructor(
    private formBuilder: FormBuilder,
    private simulatorService: PublicSimulatorService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    private authService: AuthService,
    private userService: UserService,
    @Inject(PLATFORM_ID) private platformId: object
  ) {
    this.model = this.formBuilder.group({
      pages: [100, [Validators.required, Validators.min(20)]],
      sellPrice: ['R$ 0,00', [Validators.required]],
      desiredProfit: ['R$ 0,00', [Validators.required]]
    });

    // Debounce para não ficar chamando a API de cada vez que o usuário digita páginas
    this.pagesSubject.pipe(debounceTime(500)).subscribe(pages => {
      if (pages && pages >= 20) {
        this.loadBookPrints(pages);
      }
    });
  }

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) return; // sessionStorage não existe no SSR
    window.scrollTo({ top: 0, behavior: 'smooth' });
    this.checkAccess();
  }

  /**
   * O simulador exige usuário autenticado e com os dados complementares de
   * publicação preenchidos (exceto quem já tem livro publicado).
   */
  private checkAccess(): void {
    if (!this.authService.isAuthenticated()) {
      this.openComplementaryDialog({ cadastrarUsuarioComTodasInformacoes: true });
      return;
    }

    this.userService.getSimulatorAccessStatus().subscribe({
      next: (res) => {
        if (res.isError) {
          this.router.navigate(['/']);
          return;
        }
        if (res.hasPublishedBook || res.hasPublicationProfile) {
          this.startSimulator();
          return;
        }
        this.openComplementaryDialog({
          cadastrarUsuarioComTodasInformacoes: true,
          idUser: this.authService.getUserInfos().idUser
        });
      },
      error: () => this.router.navigate(['/'])
    });
  }

  private openComplementaryDialog(data: any): void {
    const ref = this.dialog.open(UserRegisterComponent, { data });
    ref.afterClosed().subscribe(result => {
      if (result === true) {
        if (!data.idUser) {
          // Usuário recém-criado: recarrega a página para a header (menu)
          // refletir a autenticação; o ngOnInit re-executa e libera o simulador.
          window.location.reload();
          return;
        }
        // Dados complementares salvos (usuário já logado): libera direto
        this.startSimulator();
      } else if (result === 'login') {
        // Abriu o LoginComponent; login com sucesso faz window.location.reload()
        // (re-executa o ngOnInit). Se todos os dialogs fecharem sem reload, foi cancelamento.
        this.dialog.afterAllClosed.pipe(take(1)).subscribe(() => {
          if (!this.authService.isAuthenticated()) this.router.navigate(['/']);
        });
      } else {
        // Fechou sem completar (ESC/backdrop)
        this.router.navigate(['/']);
      }
    });
  }

  private startSimulator(): void {
    this.loadPublicConfig();
  }

  loadPublicConfig(): void {
    this.simulatorService.getPublicConfig().subscribe({
      next: (res) => {
        this.publicConfig = res;
        this.isLoadingConfig = false;
        this.cdr.markForCheck();

        // Dispara a busca de prints iniciais
        this.pagesSubject.next(this.model.get('pages')?.value);
      },
      error: (err) => {
        console.error('Erro ao carregar configurações públicas', err);
        this.isLoadingConfig = false;
        this.cdr.markForCheck();
      }
    });
  }

  loadBookPrints(pages: number): void {
    this.isLoadingPrints = true;
    this.cdr.markForCheck();

    this.simulatorService.getActiveBookPrintsByPages(pages).subscribe({
      next: (res) => {
        if (!res.isError && res.listBookPrintStruct) {
          this.listBookPrint = res.listBookPrintStruct;

          // Agrupar por bookPrintSize
          const map = new Map<string, BookPrintStruct[]>();
          for (const print of this.listBookPrint) {
            if (!map.has(print.bookPrintSize)) {
              map.set(print.bookPrintSize, []);
            }
            map.get(print.bookPrintSize)!.push(print);
          }
          this.groupedPrints = Array.from(map.entries())
            .map(([size, variations]) => ({
              size,
              variations: variations.sort((a, b) =>
                a.bookPrintDescription.localeCompare(b.bookPrintDescription, 'pt-BR', { numeric: true, sensitivity: 'base' })
              )
            }))
            .sort((a, b) => a.size.localeCompare(b.size, 'pt-BR', { numeric: true, sensitivity: 'base' }));

          // Se já havia um selecionado, tenta mantê-lo ou seleciona o primeiro
          if (this.groupedPrints.length > 0) {
            let preSelectGroup = this.groupedPrints[0];
            let preSelectPrint = this.groupedPrints[0].variations[0];

            if (this.selectedPrint) {
              const foundGroup = this.groupedPrints.find(g => g.size === this.selectedPrint?.bookPrintSize);
              if (foundGroup) {
                preSelectGroup = foundGroup;
                const foundPrint = foundGroup.variations.find(p => p.idBookPrint === this.selectedPrint?.idBookPrint);
                preSelectPrint = foundPrint || foundGroup.variations[0];
              }
            }

            this.selectedSizeGroup = preSelectGroup;
            this.selectedPrint = preSelectPrint;
          } else {
            this.selectedSizeGroup = null;
            this.selectedPrint = null;
          }

          this.calculateSimulation();
        }
        this.isLoadingPrints = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Erro ao carregar formatos de impressão', err);
        this.isLoadingPrints = false;
        this.cdr.markForCheck();
      }
    });
  }

  onPagesChange(): void {
    const pages = this.model.get('pages')?.value;
    if (pages && pages >= 20) {
      this.pagesSubject.next(pages);
    }
  }

  selectSizeGroup(group: PrintGroup): void {
    this.selectedSizeGroup = group;
    // Ao selecionar um novo tamanho, já pré-selecionamos a primeira variação dele para evitar estado inválido
    if (group.variations.length > 0) {
      this.selectPrint(group.variations[0]);
    } else {
      this.selectedPrint = null;
      this.calculateSimulation();
    }
  }

  selectPrint(print: BookPrintStruct): void {
    this.selectedPrint = print;
    this.calculateSimulation();
  }

  setCalcMode(mode: 'profit' | 'price'): void {
    this.calcMode = mode;
    this.userChangedPrice = false;
    this.calculateSimulation();
  }

  onCurrencyInput(event: Event, controlName: string): void {
    this.userChangedPrice = true;
    const input = (event.target as HTMLInputElement);
    const raw = input.value.replace(/\D/g, '');

    if (raw === '') {
      input.value = '';
      this.model.get(controlName)?.setValue('', { emitEvent: false });
      this.calculateSimulation(0);
      return;
    }

    const numeric = parseFloat(raw) / 100;

    const value = isNaN(numeric) ? 0 : toMoney(numeric);
    input.value = this.formatCurrency(value);

    // Atualiza o form control silenciosamente para não disparar eventos infinitos
    this.model.get(controlName)?.setValue(input.value, { emitEvent: false });

    this.calculateSimulation(value);
  }

  onCurrencyBlur(controlName: string): void {
    this.userChangedPrice = true;
    let rawValue = this.model.get(controlName)?.value;
    if (rawValue === null || rawValue === undefined) rawValue = '';

    const raw = rawValue.replace(/\D/g, '');
    let numeric = raw === '' ? 0 : parseFloat(raw) / 100;
    numeric = isNaN(numeric) ? 0 : toMoney(numeric);

    // Força o preço mínimo apenas no modo preço
    if (this.calcMode === 'price' && numeric < this.minPrice) {
      numeric = this.minPrice;
    }

    this.model.get(controlName)?.setValue(this.formatCurrency(numeric), { emitEvent: false });

    this.calculateSimulation(numeric);
  }

  formatCurrency(value: number | null): string {
    if (value == null) return '';
    return value.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
    });
  }

  getNumericValue(controlName: string): number {
    const rawValue = this.model.get(controlName)?.value || '0';
    const raw = rawValue.replace(/\D/g, '');
    if (raw === '') return 0;
    let numeric = parseFloat(raw) / 100;
    return isNaN(numeric) ? 0 : toMoney(numeric);
  }

  getSellPriceFromProfit(calc: BookCostCalc, desiredProfit: number, hasPhysicalBook: boolean): number {
    const prod = getProductionCost(calc, hasPhysicalBook);
    const r = getVariableRate(calc);
    const denom = (1 - r) <= 0 ? 0.01 : (1 - r);
    const price = (desiredProfit + prod) / denom;
    return toMoney(price);
  }

  calculateSimulation(currentValue?: number): void {
    if (!this.publicConfig || !this.selectedPrint) return;

    const calc: BookCostCalc = {
      unitCost: toMoney(this.selectedPrint.estimatedTotalCost),
      platformProfitPercent: this.publicConfig.platformProfitPercent,
      baseTaxPercent: this.publicConfig.baseTaxPercent
    };

    // 1. Calcula o preço mínimo (sempre considera physicalBook = true neste simulador)
    this.minPrice = getMinPrice(calc, true, 10);

    let finalPrice = 0;

    if (this.calcMode === 'profit') {
      let desiredProfit = currentValue !== undefined ? currentValue : this.getNumericValue('desiredProfit');

      if (!this.userChangedPrice) {
        desiredProfit = toMoney(this.minPrice * 0.5); // Lucro de exemplo (50% do custo min)
        this.model.get('desiredProfit')?.setValue(this.formatCurrency(desiredProfit), { emitEvent: false });
      }

      finalPrice = this.getSellPriceFromProfit(calc, desiredProfit, true);
      finalPrice = Math.max(finalPrice, this.minPrice);

    } else {
      let price = currentValue !== undefined ? currentValue : this.getNumericValue('sellPrice');

      if (!this.userChangedPrice) {
        price = toMoney(this.minPrice * 1.5);
        this.model.get('sellPrice')?.setValue(this.formatCurrency(price), { emitEvent: false });
      }

      finalPrice = price;
    }

    // 3. Calcula o breakdown
    const breakdown = computeBreakdown(calc, finalPrice, true);

    this.estimatedProfit = breakdown.profit;
    this.estimatedMargin = breakdown.marginPctOnBox;
    this.productionCost = breakdown.productionCost;

    // Calcula os custos proporcionais separados
    const p = toMoney(finalPrice);
    this.calculatedSellPrice = p;
    this.platformCost = toMoney(p * (this.publicConfig.platformProfitPercent / 100));
    this.taxCost = toMoney(p * (this.publicConfig.baseTaxPercent / 100));

    this.cdr.markForCheck();
  }

  publicarAgora(): void {
    window.location.href = 'https://www.feiradolivro.com.br/feiradolivro-ab';
  }

  getEstimatedClass(): string {
    if (this.estimatedProfit < 0) return 'profit-negative';
    if (this.estimatedProfit === 0) return 'profit-zero';
    return 'profit-positive';
  }
}
